import {
  type Country,
  type LinkInternal,
  type MonetaryAmount,
  cartAPI,
} from '@backmarket/http-api'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import type { EventData } from '@backmarket/nuxt-module-tracking'
import { formatPrice } from '@backmarket/nuxt-module-tracking/formatPrice'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { mapValues } from '@backmarket/utils/object/mapValues'
import { defineStore } from 'pinia'

import { getProductInfos } from '~/scopes/recommendation/helpers'

import { CHECKOUT_STEP_IDS } from '../routes-names'

import { useAddressStore } from './addressStore'
import { useDiscountStore } from './discountStore'
import { useSwapStore } from './swapStore'
import { useUserInformationStore } from './userInformationStore'

interface ShippingWIthIds extends cartAPI.Shipping {
  listingIds: string[]
  country: string
}

interface CartState {
  price: MonetaryAmount
  tax: MonetaryAmount | null
  totalPriceIncludesTax: boolean | null
  serviceFee: MonetaryAmount | null
  totalShippingPrice: MonetaryAmount
  totalPriceWithoutShipping: MonetaryAmount
  totalListingsPrice: MonetaryAmount
  totalGrossPriceWithoutAddonService?: MonetaryAmount
  totalGrossPriceWithoutBmFee?: MonetaryAmount
  items: cartAPI.CartItem[]
  shippings: cartAPI.ShippingsResponse[] | []
  lastCollectionPoint: cartAPI.LastCollectionPoint | Record<string, never>
  showCatchupModal: boolean
  isCartLoaded: boolean
}

export const useCartStore = defineStore('cart', {
  state: (): CartState => ({
    price: {
      currency: useMarketplace().market.defaultCurrency,
      amount: '0.00',
    },
    tax: {
      currency: useMarketplace().market.defaultCurrency,
      amount: '0.00',
    },
    totalPriceIncludesTax: false,
    serviceFee: null,
    totalShippingPrice: {
      currency: useMarketplace().market.defaultCurrency,
      amount: '0.00',
    },
    totalPriceWithoutShipping: {
      currency: useMarketplace().market.defaultCurrency,
      amount: '0.00',
    },
    totalListingsPrice: {
      currency: useMarketplace().market.defaultCurrency,
      amount: '0.00',
    },
    totalGrossPriceWithoutAddonService: {
      currency: useMarketplace().market.defaultCurrency,
      amount: '0.00',
    },
    totalGrossPriceWithoutBmFee: {
      currency: useMarketplace().market.defaultCurrency,
      amount: '0.00',
    },
    items: [],
    shippings: [],
    lastCollectionPoint: {},
    showCatchupModal: true,
    isCartLoaded: false,
  }),
  getters: {
    // TODO: B2CS-124: Replace the `hasMobilePlan` getter with actual check on cart items in cart store
    hasMobilePlan: () => false,
    hasServiceFee: (state) => !isEmpty(state.serviceFee),
    itemsIds: (state) => state.items.map((item) => item.listingId),
    availableItems: (state) =>
      state.items.filter((item) => item.available === 'yes'),
    areShippingsLoaded: (state) => !isEmpty(state.shippings),
    shippingsByCountry: (state) => {
      const shippingsByCountry: {
        [key: string]: { [key: string]: cartAPI.Shipping[] }
      } = {}

      state.shippings.forEach(({ listingId, countries }) => {
        countries.forEach(({ country, shippings }) => {
          if (!shippingsByCountry[country]) {
            shippingsByCountry[country] = {}
          }
          shippingsByCountry[country][listingId] = [...shippings]
          shippingsByCountry[country][listingId].sort((shippingA, shippingB) =>
            shippingA.shippingPrice > shippingB.shippingPrice ? 1 : -1,
          )
        })
      })

      return shippingsByCountry
    },
    shippingsById: (state) => {
      const shippingsById: {
        [key: string]: ShippingWIthIds
      } = {}

      state.shippings.forEach(({ listingId, countries }) => {
        countries.forEach(({ country, shippings }) => {
          shippings.forEach((shipping) => {
            if (shippingsById[shipping.shippingId]) {
              shippingsById[shipping.shippingId].listingIds.push(listingId)
            } else {
              shippingsById[shipping.shippingId] = {
                ...shipping,
                listingIds: [listingId],
                country,
              }
            }
          })
        })
      })

      return shippingsById
    },

    /**
     * @returns A map containing all selected options, indexed by item id.
     */
    selectedOptionsByItemId: (
      state,
    ):
      | Record<
          string,
          Array<{ choice: cartAPI.ShippingChoice } & cartAPI.ShippingOption>
        >
      | Record<string, never> =>
      state.items.reduce(
        (acc, item) => ({
          ...acc,
          [item.listingId]: item.options
            .map((option) => ({
              option,
              choice: option.choices.find(({ selected }) => selected),
            }))
            .filter(
              // Ensure choice was found
              ({ choice }) => !isEmpty(choice),
            ),
        }),
        {},
      ),
    priceAfterDiscount(): MonetaryAmount {
      const { isApplied, getDeductedPrice } = useDiscountStore()

      return isApplied ? getDeductedPrice : this.price
    },
    /**
     * @returns {boolean} Returns true if the current checkout state is shippable.
     * For regular shipping:
     * - shipping address is valid (see isShippingAddressComplete in the address store)
     * - cart items are shippable for the current shipping address country (see
     *   isShippableForCountry)
     * For collection point shipping:
     * - customer details are valid (see isCollectionPointAddressComplete in the address store)
     * - note: no check is performed for the selected collection point, that is
     *         a separate check, which is used for a different redirection (see
     *         isSelectedCollectionPointMissing).
     */
    isShippable(): boolean {
      const {
        hasCollectionPoint,
        isBillingAddressComplete,
        isCollectionPointAddressComplete,
        isShippingAddressComplete,
        shipping,
      } = useAddressStore()

      const isShippable = hasCollectionPoint
        ? isCollectionPointAddressComplete
        : isShippingAddressComplete &&
          this.isShippableForCountry(shipping.country as Country)

      return isShippable && isBillingAddressComplete
    },
    availableItemsById(): { [key: string]: cartAPI.CartItem } {
      return this.availableItems.reduce(
        (acc, item) => ({ ...acc, [item.listingId]: item }),
        {},
      )
    },
    hasAvailableItems(): boolean {
      return !isEmpty(this.availableItems)
    },
    availableItemsCount(): number {
      return this.availableItems.reduce((sum, item) => sum + item.quantity, 0)
    },
    /**
     * @returns A map containing all selected shippings, indexed by item id.
     */
    selectedShippingsByAvailableItemId(): { [key: string]: ShippingWIthIds } {
      return mapValues(
        this.availableItemsById,
        (item) => this.shippingsById[item.shippingSelected.shippingFeePk],
      )
    },
    /**
     * @returns A function that returns `true` if
     * all items are shippable for a given country (ex: 'FR'). This means that
     * each selected shipping must match that country.
     */
    isShippableForCountry(): (country: Country) => boolean {
      return (country: Country) => {
        return Object.values(this.selectedShippingsByAvailableItemId).every(
          (shipping) => shipping && shipping.country === country,
        )
      }
    },
    /**
     * @returns insurance offers that have been selected
     */
    selectedInsuranceOffers(): cartAPI.CheckoutInsuranceOffer[] {
      return this.availableItems
        .map((item: cartAPI.CartItem) =>
          item.insuranceOffers?.find((offer) => offer.selected),
        )
        .filter(
          (offer): offer is cartAPI.CheckoutInsuranceOffer =>
            !offer?.defaultOffer,
        )
    },
    shippingsByItemId(): { [key: string]: ShippingWIthIds } {
      if (!this.areShippingsLoaded) {
        return {}
      }

      return mapValues(
        this.availableItemsById,
        (item) => this.shippingsById[item.shippingSelected.shippingFeePk],
      )
    },
    /**
     * @returns A function that returns an object containing all items that are not shippable, indexed by item id.
     */
    getUnshippableItems(): (addressCountry: string) => {
      [key: string]: cartAPI.CartItem
    } {
      return (addressCountry: string) => {
        const { market } = useMarketplace()
        if (addressCountry === '') {
          return {}
        }

        return Object.entries(this.shippingsByItemId).reduce(
          (acc, [itemId, shipping]) =>
            shipping?.country === market.countryCode &&
            addressCountry === market.countryCode
              ? acc
              : { ...acc, [itemId]: this.availableItemsById[itemId] },
          {},
        )
      }
    },
    hasUnshippableItemsForCountry(): (country: string) => boolean {
      return (country: string) => !isEmpty(this.getUnshippableItems(country))
    },
    hasCollectionPointShipping(): boolean {
      return Object.values(this.selectedOptionsByItemId).some((options) =>
        options.some(
          ({ choice }: { choice: cartAPI.ShippingChoice }) =>
            choice.isCollectionPoint,
        ),
      )
    },
    optionWithMissingCollectionPointSelected():
      | [
          string,
          ({
            choice: cartAPI.ShippingChoice
          } & cartAPI.ShippingOption)[],
        ]
      | undefined {
      return !this.hasSelectedCollectionPoint
        ? Object.entries(this.selectedOptionsByItemId).find(
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            ([, option]) =>
              option.some(
                ({ choice }: { choice: cartAPI.ShippingChoice }) =>
                  choice.isCollectionPoint,
              ),
          )
        : undefined
    },

    selectedCollectionPoint(): cartAPI.CartCollectionPoint {
      return this.lastCollectionPoint?.selectedPoint
    },
    hasSelectedCollectionPoint(): boolean {
      return !isEmpty(this.selectedCollectionPoint)
    },
    isSelectedCollectionPointMissing(): boolean {
      return this.hasCollectionPointShipping && !this.hasSelectedCollectionPoint
    },
    isItemAvailable() {
      return (listingId: string): boolean =>
        !!this.availableItemsById[listingId]
    },
    getItemLink() {
      return (listingId: string): LinkInternal => {
        return {
          ...this.availableItemsById[listingId]?.link,
          params: {
            locale: this.availableItemsById[listingId]?.link?.params?.locale,
            slugV2: this.availableItemsById[listingId]?.link?.params?.slugV2,
            uuid: this.availableItemsById[listingId]?.link?.params?.uuid,
          },
        }
      }
    },
    hasInsurance() {
      return this.selectedInsuranceOffers.length > 0
    },

    trackingData() {
      return (
        routeName: string,
      ): {
        products: EventData[]
        swap: boolean
        step: number
        pageType: string
      } => {
        const swapStore = useSwapStore()
        const { defaultCurrency } = useMarketplace().market

        return {
          products: this.items.map((item) => {
            const insurance = item.insuranceOffers?.find(
              (offer) => !offer.defaultOffer && offer.selected,
            )

            const deliveryOption = item.options.find(
              (option) => option.type === 'delivery',
            )
            const deliveryOptionChoices = deliveryOption?.choices || []

            return {
              available: item.available === 'yes',
              brand: item.brand ?? '',
              category: item.category ?? '',
              color: item.color ?? '',
              currency: defaultCurrency,
              id: item.productId ?? '',
              insurancePrice: insurance?.price.amount ?? '',
              insuranceTitle: insurance?.title ?? '',
              list: getProductInfos(item.productId).source,
              listingId: item.listingId ?? '',
              merchantId: item.merchantId ?? '',
              model: item.model ?? '',
              name: item.title ?? '',
              specialOfferType: item.specialOfferType,
              price: item.price ?? '',
              quantity: item.quantity ?? '',
              shipper: [
                item.shippingSelected.shipperId ?? '',
                item.shippingSelected.shippingFeeDelay ?? '0',
                formatPrice({
                  amount: item.shippingSelected.shippingFeePrice,
                  currency: defaultCurrency,
                }),
              ].join(' - '),
              shippingDelay: item.shippingSelected.shippingFeeDelay ?? '',
              shippingPrice: item.shippingSelected.shippingFeePrice ?? '',
              shippingOptions: deliveryOptionChoices.map((choice) =>
                [
                  choice.shipperId ?? '',
                  choice.shippingDelay ?? '0',
                  formatPrice({
                    amount: choice.price,
                    currency: defaultCurrency,
                  }),
                ].join(' - '),
              ),
              uuid: item.akeneoId ?? '',
              variant: item.gradeId ?? '',
              warrantyDuration: item.warranties[0]?.delay ?? '',

              dealType: 'normal',
              isInsuranceEligible: !!item.insuranceOffers?.length || false,
            }
          }),
          swap: swapStore.hasOffer,
          step: CHECKOUT_STEP_IDS[routeName] ?? '',
          pageType: routeName ?? '',
        }
      }
    },
  },
  actions: {
    setPrice({
      totalListingsPrice,
      totalPrice,
      serviceFee,
      tax,
      totalDelivery,
      totalGrossPriceWithoutAddonService,
      totalGrossPriceWithoutBmFee,
      totalPriceIncludesTax,
    }: cartAPI.CartSummary) {
      // Picking up currency from the total listing since we don't have a standalone currency prop
      const { currency } = totalListingsPrice

      this.price = { amount: totalPrice, currency }
      this.serviceFee = isEmpty(serviceFee) ? null : serviceFee
      this.tax = tax
      this.totalShippingPrice = totalDelivery
      this.totalGrossPriceWithoutAddonService =
        totalGrossPriceWithoutAddonService
      this.totalGrossPriceWithoutBmFee = totalGrossPriceWithoutBmFee
      this.totalListingsPrice = totalListingsPrice
      this.totalPriceIncludesTax = totalPriceIncludesTax
    },
    setItems(items: cartAPI.CartItem[]) {
      this.items = items
      this.shippings = []
    },
    setShippings(shippings: cartAPI.ShippingsResponse[]) {
      this.shippings = shippings
    },
    setLastCollectionPoint(
      lastCollectionPoint: cartAPI.LastCollectionPoint | Record<string, never>,
    ) {
      this.lastCollectionPoint = isEmpty(lastCollectionPoint)
        ? {}
        : lastCollectionPoint
    },
    setShowCatchupModal(showCatchupModal: boolean) {
      this.showCatchupModal = showCatchupModal
    },
    setCart(payload: cartAPI.CartResponse) {
      this.setPrice(payload.summary)
      this.setItems(payload.cartItems)
      this.setShippings([])
      this.setLastCollectionPoint(payload.lastCollectionPoint)
    },
    skipCatchupModal() {
      this.setShowCatchupModal(false)
    },
    async fetchShippings() {
      if (!this.areShippingsLoaded) {
        const shippings = await $httpFetch(cartAPI.getShippings)

        this.setShippings(shippings)
      }
    },
    async fetchCart() {
      const experiments = useExperiments()

      const queryParams =
        experiments['experiment.serviceFeePriceGroup24'] === 'newPriceGrid24'
          ? { newPriceGrid24: true }
          : {}

      const { setAddress } = useAddressStore()
      const { setSwap } = useSwapStore()
      const { setBasePrice } = useDiscountStore()
      const { setUserInformation } = useUserInformationStore()

      const cart = await $httpFetch(cartAPI.getCart, {
        queryParams,
      })

      this.setCart(cart)
      setAddress(cart)
      setSwap(cart.swap)
      setUserInformation(cart.userInformation)
      setBasePrice({
        amount: cart.summary.totalPrice,
        currency: this.price.currency,
      })

      this.isCartLoaded = true
    },
  },
})
